import { AxiosResponse } from 'axios';
import { BlockContent } from 'components/blocks/BlockContent';
import { BlockHeader } from 'components/blocks/BlockHeader';
import { BoxWithShadow } from 'components/blocks/BoxWithShadow';
import { Twitter } from 'components/common/Twitter';
import { formatAgoTimestampOrString } from 'util/formatAgo';
import { LdJsonBreadCrumbsResort, LdJsonSkiResort } from 'components/LdJson';
import { SidebarAd } from 'googlead/components/SidebarAd';
import { useMustacheTranslation, useOTSTranslation } from 'i18n/useOTSTranslation';
import { RouteAndAnalyticsProps } from 'page-components/pathTypes';
import { Routes } from 'routes';
import { NextLinkTr } from 'components/blocks/NextLink';
import { getResortSlug } from 'util/memcache';
import { SnowReportComments } from 'components/resort/SnowReportComments';
import { ResortOverview } from 'components/resort/ResortOverview';
import { HeaderNavCurLink } from 'components/common/HeaderNav';
import {
  fetchResortFeaturedCam,
  fetchResortRelated,
  fetchResortRelatedNews,
  fetchResortReviews,
  fetchResortShortInfo,
  fetchResortShortWeatherReport,
  fetchResortSnowReport,
  fetchResortDescriptionTabs,
  fetchResortLastFHReport,
} from 'api/resort/static';
import {
  IRelatedNews,
  IResortPageInfo,
  IResortPageSnowInfo,
  IResortPathInfo,
  IDescriptionsResort,
  IReviewsGeneral,
  OpenFlag,
  IFirstHandReport,
} from 'api/resort/types';
import { IWebcamItem } from 'api/region/types';
import { CurrentLink, CurrentTab } from 'components/resort/ResortHeaderNav/constants';
import { Main } from 'components/resort/Layout/Main';
import { Side } from 'components/resort/Layout/Side';
import { RecentSnowfall } from 'components/resort/RecentSnowfall';
import { ResortStatus } from 'components/resort/ResortStatus';
import PlanYourTrip from 'components/common/PlanYourTrip/ResortWidget';
import SurfaceRelatedArticles from 'components/resort/SurfaceRelatedArticles';
import { RegularAd } from 'googlead/components/RegularAd';
import { FeaturedCamera } from 'components/common/FeaturedCamera';
import { Hero400Error } from 'components/400';
import { fetchResortMeta } from 'api/region/static';
import { SHORT_DATE_FORMAT, formatDateFromStr } from 'util/formatDate';
import { useDistanceFormatter } from 'i18n/unitFormat';
import { assertShouldBeNot404, wrapServerSideProps, wrapStaticProps } from 'util/wrapProps';
import { RelatedRegionResortList } from 'components/resort/RelatedRegionResortList';
import classNames from 'classnames';
import cameraStyles from 'components/common/FeaturedCamera/styles.module.scss';
import { MetricBox } from 'components/blocks/MetricBox';
import { LiftTerrainStatus } from 'components/resort/LiftTerrainStatus';
import { SnowDepth } from 'components/resort/SnowDepth';
import { ResortLastHandReport } from 'components/resort/ResortLastHandReport';
import { useState, useCallback } from 'react';
import { PopupFeedback } from 'components/resort/PopupFeedback';
import { SvgLinkTr } from 'components/icons';
// import { NearbyResortsSection } from 'components/resort/NearbyResortsSection';
import { IResortLayout } from './_layout';

import styles from './skireport.module.scss';

export type ResortSkiReportPageProps = {
  featured: IWebcamItem | null,
  resort: IResortPageInfo,
  fullResort: IResortPageSnowInfo,
  pathInfo: IResortPathInfo,
  reviewsGeneral: IReviewsGeneral | null,
  resortDescriptionTabs: Array<IDescriptionsResort>,
  relatedNews: IRelatedNews | null,
  firstHand: IFirstHandReport | null
} & RouteAndAnalyticsProps & IResortLayout;

export const DEBUG_TAG = 'resort-skireport-page';
export const TEMPLATE_ID = '2.2.1';
export const TEMPLATE_NAME = 'Resort Ski Report';

export default function ResortSkiReportPage({
  featured,
  fullResort,
  pathInfo,
  reviewsGeneral,
  related,
  firstHand,
  resortDescriptionTabs,
  relatedNews,
}: ResortSkiReportPageProps) {
  const [isOpenPopup, toggleIsOpenPopup] = useState<boolean>(false);

  const togglePopup = useCallback(() => {
    toggleIsOpenPopup(!isOpenPopup);
  }, [isOpenPopup]);

  const { t } = useOTSTranslation('common');
  const m = useMustacheTranslation('common');
  const dt = useDistanceFormatter('cmOrInchShort');

  if (!fullResort) {
    return <Hero400Error light />;
  }

  const resortCountry = fullResort?.region?.country_code;
  const openFlags = [OpenFlag.Open, OpenFlag.WeekendsOnly, OpenFlag.TemporarilyClosed, OpenFlag.NoReportAvailable];
  const isOpen = fullResort.status.openFlag && openFlags.includes(fullResort.status.openFlag);

  // Recent snowfall and forecast data
  const chartDataRecent = fullResort.recent ? fullResort.recent.slice(-6) : [];
  const chartDataForecast = fullResort.forecast ? fullResort.forecast.slice(0, 6) : [];
  const maxY = [...chartDataRecent, ...chartDataForecast].reduce((val, item) => Math.max(item.snow, val), 20);

  // Pull out resort overview text
  const resortOverview = resortDescriptionTabs.filter((item) => item.tab === 'resort_profile');
  const resortOverviewText = resortOverview.length > 0 ? resortOverview[0].description : null;

  return (
    <>
      {isOpenPopup && (
      <PopupFeedback
        togglePopup={togglePopup}
        resort={fullResort.title}
        id={fullResort.uuid}
      />
      )}
      <Main>
        <BoxWithShadow>
          <BlockHeader title={t('resort.skiReport.seoTitle', { resort: fullResort.title })} />
          <BlockContent>
            {m('resort.skiReport.seoText', {
              resort: fullResort.title,
              depth: dt([fullResort.depths?.base || fullResort.depths?.summit], '-'),
              base_depth_cm: dt(fullResort.depths?.base),
              top_depth_cm: dt(fullResort.depths?.summit),
              base_depth: dt(fullResort.depths?.base),
              top_depth: dt(fullResort.depths?.summit),
              unitMeasurement: '',
              lifts_open: fullResort.lifts?.open,
              num_lifts: fullResort.lifts?.total,
              created_dt: formatDateFromStr(fullResort.updatedAt, SHORT_DATE_FORMAT),
            })}
          </BlockContent>
        </BoxWithShadow>

        <div className={classNames(styles.reportContent)}>
          <ResortStatus fullResort={fullResort} />

          <div className={styles.innerLayout} style={{ paddingBottom: 8, marginBottom: 0 }}>
            <div className={classNames(styles.innerLeft)}>
              <MetricBox
                title={t('resort.skiReport.recentSnowfall')}
              >
                <RecentSnowfall
                  data={chartDataRecent}
                  maxY={maxY}
                  show24Hour
                />
              </MetricBox>
            </div>

            <div className={classNames(styles.innerLeft)}>
              <MetricBox
                title={t('resort.skiReport.forecastedSnow')}
                link={(
                  <NextLinkTr href={Routes.resortWeather(pathInfo)} data-click-id="resort-report-weather">
                    {t('resort.skiReport.fullWeatherForecast')}
                  </NextLinkTr>
              )}
              >
                <RecentSnowfall
                  data={chartDataForecast}
                  maxY={maxY}
                />
              </MetricBox>
            </div>
          </div>

          {isOpen && (<SnowDepth fullResort={fullResort} pathInfo={pathInfo} />)}
          {fullResort.comment && <SnowReportComments isFresh comments={fullResort.comment} />}
          {isOpen && (<LiftTerrainStatus fullResort={fullResort} resortCountry={resortCountry} />)}
          <button
            type="button"
            className={styles.fhBtnPopup}
            onClick={togglePopup}
          >
            {t('resort.skiReport.provideFeedback')}
            <SvgLinkTr />
          </button>
        </div>

        <RegularAd isFirst />

        <div className={styles.innerLayout}>
          <div className={classNames(styles.innerLeft)}>
            { firstHand && (
              <ResortLastHandReport resort={pathInfo} firstHand={firstHand} />
            )}
          </div>

          <div className={classNames(styles.innerLeft)}>
            { featured && (
              <BoxWithShadow>
                <BlockHeader useH3 title={t('resort.cams.heroTitle')}>
                  <NextLinkTr href={Routes.resortCams(pathInfo)} data-click-id="resort-report-skicams">
                    {t('common.seeAllCams')}
                  </NextLinkTr>
                </BlockHeader>
                <BlockContent>
                  <FeaturedCamera
                    isFresh
                    cam={featured}
                    href={Routes.resortCams(pathInfo)}
                    className={cameraStyles.skireportView}
                    hideTitle
                  />
                  <div>{featured.title}</div>
                  <div className={styles.camDate}>
                    {formatAgoTimestampOrString(featured.date)}
                  </div>

                </BlockContent>
              </BoxWithShadow>
            )}
          </div>
        </div>

        {resortOverviewText && (
          <ResortOverview
            fullResort={fullResort}
            overview={resortOverviewText}
            pathInfo={pathInfo}
          />
        )}
        <RegularAd />

        <div id="wisepops-embed" />

        <PlanYourTrip
          resort={pathInfo}
          hasSkiRental={pathInfo.hasSkiRental}
          hasFlights={pathInfo.hasFlights}
          hasCarRental={pathInfo.hasCarRental}
          liftsUrl={fullResort.liftsUrl}
          rentalUrl={fullResort.rentalUrl}
          lessonsUrl={fullResort.lessonsUrl}
          isPartner={fullResort.isPartner}
          resortCountry={resortCountry}
        />

        {related.nearby && related.related && (
          <RelatedRegionResortList
            title={t('common.relatedresorts')}
            regions={related.related}
            resorts={related.nearby}
            isFresh
          />
        )}

      </Main>
      <Side>
        {fullResort.twitter && <Twitter feed={fullResort.twitter} />}
        <SurfaceRelatedArticles
          relatedNewsList={relatedNews}
          isFresh
        />
        <SidebarAd />
      </Side>
      <LdJsonSkiResort
        schedule={fullResort.schedule}
        resort={fullResort}
        averageReviews={reviewsGeneral}
      />
      <LdJsonBreadCrumbsResort
        resort={pathInfo}
        region={fullResort.region}
        fn={Routes.resortSnowReport}
      />
    </>
  );
}

export const fetchProps = async ({ resort }: { region: string, resort: string }): Promise<ResortSkiReportPageProps> => {
  const cache: AxiosResponse<IResortPathInfo> = await assertShouldBeNot404(() => getResortSlug(resort));
  const { uuid } = cache.data;
  const res = await fetchResortShortInfo(uuid);
  const fullResort = await fetchResortSnowReport(uuid);
  const reviewsGeneral = await fetchResortReviews(uuid);
  const relatedNews = await fetchResortRelatedNews(uuid);
  const featured = await fetchResortFeaturedCam(uuid);
  const resortDescriptionTabs = await fetchResortDescriptionTabs(uuid);
  const firstHand = await fetchResortLastFHReport(uuid);

  return {
    type: 'resort',
    route: 'skireport',
    resort: res,
    shortWeather: await fetchResortShortWeatherReport(uuid),
    fullResort,
    pathInfo: cache.data,
    reviewsGeneral,
    relatedNews,
    resortDescriptionTabs,
    featured,
    firstHand,

    /**
         * Layout
         */
    related: await fetchResortRelated(uuid),
    navigation: {
      link: CurrentLink.SNOW_REPORT,
      tab: CurrentTab.SNOW,
      head: HeaderNavCurLink.SNOW_REPORTS,
    },
    templateName: TEMPLATE_NAME,
    templateId: TEMPLATE_ID,
    adTKey: 'resort skireport',
    hero: {
      title: 'resort.skiReport.heroTitle',
      showVideo: true,
    },
    meta: {
      description: 'Resort.skireport.description',
      redirectTo: cache.data.domain ?? null,
      title: 'Resort.skireport.title',
      dynamic: (await fetchResortMeta(uuid)),
    },
  };
};

export const getResortSkiReportPageStaticProps = wrapStaticProps(fetchProps);
export const getResortSkiReportPageServerSideProps = wrapServerSideProps(fetchProps);
